import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * Listens for variant updates and lens customization surcharge changes.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {number} */
  #lensSurcharge = 0;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener('lens:price-change', this.updateLensPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener('lens:price-change', this.updateLensPrice);
  }

  /**
   * @returns {number}
   */
  #basePriceCents() {
    return Number(this.dataset.basePrice) || 0;
  }

  /**
   * @param {number} cents
   */
  #formatCents(cents) {
    const currency = this.dataset.currency || '';
    const withCurrency = this.dataset.moneyWithCurrency === 'true';
    const format = withCurrency
      ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '{{amount}}'
      : this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(cents, format, currency);
  }

  #applyTotal() {
    const total = this.#basePriceCents() + (Number(this.#lensSurcharge) || 0);
    const priceEl = this.querySelector('[ref="priceContainer"] .price');
    if (!priceEl) return;
    priceEl.textContent = this.#formatCents(total);
  }

  /**
   * Updates the displayed total when a lens package surcharge changes.
   * @param {CustomEvent} event
   */
  updateLensPrice = (event) => {
    const detail = event.detail || {};
    if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;
    this.#lensSurcharge = Number(detail.surcharge) || 0;
    this.#applyTotal();
  };

  /**
   * Updates the price and volume pricing note from a variant update.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    if (newProductPrice.dataset.basePrice != null) {
      this.dataset.basePrice = newProductPrice.dataset.basePrice;
    }

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Keep any active lens surcharge applied on top of the new variant base price.
    if (this.#lensSurcharge) this.#applyTotal();
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
